import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2, type LucideIcon } from 'lucide-react'
import { forwardRef, type ComponentPropsWithoutRef } from 'react'

import { cn } from './cn'
import { useDisabled } from './control-scope'
import { FOCUS_RING } from './recipes'
import { Tooltip, type TooltipSide } from './Tooltip'

/**
 * Everything the button looks like, in one place.
 *
 * A restyle is a diff in this recipe and in the tokens it names — never in a
 * screen. The colours come from the CSS custom properties in `global.css`, so
 * `bg-primary` here and the navy in the design are the same declaration.
 */
const button = cva(
  'inline-flex items-center justify-center gap-1.5 rounded-md font-medium ' +
    'whitespace-nowrap transition-colors select-none ' +
    `${FOCUS_RING} ` +
    'disabled:opacity-40 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-primary-foreground hover:bg-destructive/90',
        outline:
          'border border-border bg-card text-foreground hover:bg-line-soft',
        secondary: 'bg-line-soft text-foreground hover:bg-border/70',
        ghost: 'text-foreground-2 hover:bg-line-soft',
        'ghost-destructive': 'text-destructive hover:bg-destructive-soft',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-7 px-2.5 text-xs',
        md: 'h-8 px-3 text-[13px]',
        lg: 'h-9 px-5 text-[13px]',
        icon: 'h-7 w-7',
      },
    },
    defaultVariants: { variant: 'default', size: 'md' },
  },
)

/** Icon sizes that sit right in each button height. */
const ICON_SIZE = { sm: 12, md: 14, lg: 15, icon: 14 } as const

type ButtonProps = ComponentPropsWithoutRef<'button'> &
  VariantProps<typeof button> & {
    /** Drawn before the label. */
    icon?: LucideIcon
    /** Drawn after the label — a chevron, an external-link mark. */
    iconRight?: LucideIcon
    /** Replaces the leading icon with a spinner and blocks the press. */
    isLoading?: boolean
    /**
     * Names the button on hover, and to assistive tech. An icon-only button in
     * a control strip needs both, and writing them out separately is how a
     * label and its tooltip came to say different things.
     */
    tooltip?: string
    /** Where the tooltip opens. Above, unless there is nothing above. */
    tooltipSide?: TooltipSide
    /**
     * A toggle that is currently on — the panel it opens is showing, the
     * filter it sets is applied. Draws the held-down ground and says so
     * through `aria-pressed`, which is what a screen reader needs to tell a
     * toggle from a button that fires and forgets.
     */
    pressed?: boolean
    /**
     * Marks a button that changes nothing — a filter, a way back, a dialog it
     * closes — so a read-only surface leaves it working. Everything else is
     * disabled inside one, which is the safe direction to be wrong in.
     */
    reads?: boolean
  }

/**
 * A real `<button>`.
 *
 * Keyboard activation, `disabled`, form submission and focus order come from
 * the element rather than from props that reimplement them.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant,
      size,
      icon: Icon,
      iconRight: IconRight,
      isLoading,
      disabled,
      children,
      type = 'button',
      tooltip,
      tooltipSide = 'top',
      pressed,
      reads,
      ...rest
    },
    ref,
  ) {
    const glyph = ICON_SIZE[size ?? 'md']
    const refused = useDisabled() && !reads

    const element = (
      <button
        ref={ref}
        type={type}
        disabled={disabled || isLoading || refused}
        aria-label={tooltip}
        aria-pressed={pressed}
        className={cn(
          button({ variant, size }),
          pressed && 'bg-border text-foreground hover:bg-border',
          className,
        )}
        {...rest}
      >
        {isLoading ? (
          <Loader2 size={glyph} className="animate-spin" aria-hidden />
        ) : Icon ? (
          <Icon size={glyph} aria-hidden />
        ) : null}
        {children}
        {IconRight ? <IconRight size={glyph} aria-hidden /> : null}
      </button>
    )

    return (
      <Tooltip label={tooltip} side={tooltipSide}>
        {element}
      </Tooltip>
    )
  },
)

export { button as buttonRecipe }
